import { Link, createFileRoute } from "@tanstack/react-router";

import ScheduledTasks from "@/components/ScheduledTasks";
import { AppHeader } from "@/components/app-header";
import { RequireAuth } from "@/components/route-guards";
import { useAuth } from "@/lib/auth";
import { signOut } from "@/lib/platform-fetch";

/**
 * `/scheduled-tasks` -- the caller's recurring tasks (docs/SCHEDULED_TASKS.md, G97).
 *
 * A top-level route rather than one under `/settings`, because a scheduled task is work the
 * platform does on your behalf, not a preference. It is reached from the sidebar, next to the
 * conversations it creates.
 *
 * Guarded by `RequireAuth` plus `canScheduleTasks` -- `Role.may_schedule_tasks` and the platform
 * switch, folded together by `/v1/me`. Unlike `/settings/skills`, there is no useful page for
 * someone without the grant: every route it calls 404s, and a task they cannot own is not a task
 * they can pause. So this one explains rather than renders.
 */
export const Route = createFileRoute("/scheduled-tasks")({
  component: ScheduledTasksRoute,
  /** `?task=<id>` opens that task's edit dialog once the list loads -- the sidebar's "Edit
   * task" on a scheduled thread links here. Validated loosely on purpose: an unknown id is the
   * page's problem to report, not the router's to reject, and a 404-shaped router error would
   * replace the whole page with nothing actionable. */
  validateSearch: (search: Record<string, unknown>): { task?: string } => ({
    task: typeof search.task === "string" ? search.task : undefined,
  }),
});

function ScheduledTasksRoute() {
  return (
    <RequireAuth>
      <ScheduledTasksPage />
    </RequireAuth>
  );
}

function ScheduledTasksPage() {
  const { user, hasAdminAccess, skillsEnabled, canScheduleTasks } = useAuth();
  const { task } = Route.useSearch();
  const navigate = Route.useNavigate();

  // Deep-linking here without the grant lands on an explanation rather than a page whose every
  // request 404s. The routes refuse regardless -- this is the readable half.
  if (!canScheduleTasks) {
    return (
      <main className="flex min-h-dvh flex-col">
        <AppHeader
          left={<Link to="/">&larr; Back to chat</Link>}
          userLabel={user?.email ?? user?.name ?? "unknown"}
          hasAdminAccess={hasAdminAccess}
          skillsEnabled={skillsEnabled}
          signOutAction={signOut}
        />
        <div className="mx-auto w-full max-w-2xl px-4 py-10">
          <h1 className="text-xl font-semibold">Scheduled</h1>
          <p className="text-muted-foreground mt-2 text-sm">
            Scheduled tasks are not available for your account. Ask an administrator if you need
            them.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-dvh flex-col">
      <AppHeader
        left={<Link to="/">&larr; Back to chat</Link>}
        userLabel={user?.email ?? user?.name ?? "unknown"}
        hasAdminAccess={hasAdminAccess}
        skillsEnabled={skillsEnabled}
        signOutAction={signOut}
      />
      <ScheduledTasks
        openTaskId={task}
        clearOpenTask={() =>
          // `replace`, so Back returns to the chat the user came from rather than to this same
          // page with the dialog reopening.
          void navigate({ search: {}, replace: true })
        }
      />
    </main>
  );
}
